import React, { useMemo } from "react"
import { Card } from "react-bootstrap"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { WithChildren } from "shared/types/WithChildren"
import { WithClassName } from "shared/types/WithClassName"
import { InfoTip } from "shared/ui/info-tip/InfoTip"
import { MediaOption } from "shared/ui/media-option/MediaOption"

import "./styles/_building-reference-card.scss"

interface BuildingReferenceCardRootProps extends WithClassName, WithChildren {}

const BuildingReferenceCardRoot = React.memo(
  ({ className, children }: BuildingReferenceCardRootProps) => {
    return (
      <Card
        className={classNames(
          "building-reference-card card--hover-within",
          className
        )}
      >
        {children}
      </Card>
    )
  }
)

interface BuildingReferenceCardTitleProps extends WithClassName, WithChildren {}

const BuildingReferenceCardTitle = React.memo(
  ({ className, children }: BuildingReferenceCardTitleProps) => {
    return (
      <Card.Title
        className={classNames("building-reference-card__title", className)}
      >
        {children}
      </Card.Title>
    )
  }
)

interface BuildingReferenceCardImageProps extends WithClassName, WithChildren {}

const BuildingReferenceCardImage = React.memo(
  ({ className, children }: BuildingReferenceCardImageProps) => {
    return (
      <div className={classNames("building-reference-card__image", className)}>
        {children}
      </div>
    )
  }
)

const BuildingReferenceCardInfoText = React.memo(
  ({ className, children }: BuildingReferenceCardRootProps) => {
    return (
      <div
        className={classNames("building-reference-card__info-text", className)}
      >
        {children}
      </div>
    )
  }
)

interface BuildingReferenceCardProductListProps extends WithClassName {
  products:
    | { title: string; kind: string; image: React.ReactNode }[]
    | undefined
}

const BuildingReferenceCardProductList = React.memo(
  ({ products, className }: BuildingReferenceCardProductListProps) => {
    const { t } = useTranslation()

    const { previewProducts, additionalProducts } = useMemo(() => {
      return {
        previewProducts: products?.slice(0, 2) || [],
        additionalProducts: products?.slice(2) || []
      }
    }, [products])

    if (!products || previewProducts.length === 0) {
      return null
    }

    const additionalProductsText = t("General.PlusAdditional", {
      count: additionalProducts.length
    })

    return (
      <div
        className={classNames(
          "building-reference-card__product-list",
          className
        )}
      >
        {previewProducts.map(product => (
          <MediaOption.Root variant="product" key={product.title}>
            <MediaOption.Media>{product.image}</MediaOption.Media>
            <MediaOption.Label>{product.title}</MediaOption.Label>
            <MediaOption.Subtext>{product.kind}</MediaOption.Subtext>
          </MediaOption.Root>
        ))}

        <div className="visually-hidden">{additionalProductsText}</div>

        {additionalProducts.length > 0 ? (
          <InfoTip
            label={additionalProductsText}
            className="building-reference-card__additional-products btn--bleed"
            popupClassName="building-reference-card__products-popup"
            side="right"
            tabIndex={-1}
            content={
              <div className="building-reference-card__products-popup building-reference-card__product-list">
                {additionalProducts.map(product => (
                  <MediaOption.Root variant="product" key={product.title}>
                    <MediaOption.Media>{product.image}</MediaOption.Media>
                    <MediaOption.Label>{product.title}</MediaOption.Label>
                    <MediaOption.Subtext>{product.kind}</MediaOption.Subtext>
                  </MediaOption.Root>
                ))}
              </div>
            }
            aria-hidden
          >
            {additionalProductsText}
          </InfoTip>
        ) : null}
      </div>
    )
  }
)

const BuildingReferenceCard = {
  Root: BuildingReferenceCardRoot,
  Title: BuildingReferenceCardTitle,
  Image: BuildingReferenceCardImage,
  InfoText: BuildingReferenceCardInfoText,
  ProductList: BuildingReferenceCardProductList
}

export { BuildingReferenceCard }
